/**
 * useAlertTrigger
 *
 * Mounted once at the app root. Polls the next departure for each active route alert
 * every 30s. When the time-until-departure crosses the lead-time threshold, fires an
 * in-app toast and badges the Varsler tab. Deduplication uses
 * `last_triggered_departure_id` so the same departure never fires twice.
 *
 * V1 in-app only. Architecture is push-ready: the same dedup key + edge function
 * fields can drive a server-side cron later.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatClock, formatCountdownMinutes, formatMessage } from '@/lib/timeFormat';
import { useRouteAlerts, type RouteAlert } from '@/hooks/useRouteAlerts';
import { useFerryRoutes } from '@/hooks/useFerryRoutes';
import { edgeFn, EdgeFnError } from '@/lib/edgeFn';
import { findMatchingStop } from '@/lib/dockMatch';
import { trackEvent } from '@/lib/analytics';

const POLL_INTERVAL_MS = 60_000; // every minute
const TRIGGER_WINDOW_MS = 90_000; // ±90s window around target time
const ALERT_BADGE_KEY = 'routeAlertsUnseen_v1';
const ALERT_BADGE_EVENT = 'routeAlertsBadgeUpdated';

interface NearestStop {
  name: string;
  departures?: Array<{ time: string; destination: string; line: string; lineId?: string }>;
}

function bumpUnseenBadge() {
  if (typeof window === 'undefined') return;
  try {
    const cur = parseInt(localStorage.getItem(ALERT_BADGE_KEY) || '0', 10) || 0;
    localStorage.setItem(ALERT_BADGE_KEY, String(cur + 1));
    window.dispatchEvent(new CustomEvent(ALERT_BADGE_EVENT));
  } catch { /* ignore */ }
}

export function clearAlertBadge() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(ALERT_BADGE_KEY, '0');
    window.dispatchEvent(new CustomEvent(ALERT_BADGE_EVENT));
  } catch { /* ignore */ }
}

export function getAlertBadgeCount(): number {
  if (typeof window === 'undefined') return 0;
  try {
    return parseInt(localStorage.getItem(ALERT_BADGE_KEY) || '0', 10) || 0;
  } catch {
    return 0;
  }
}

export const ALERT_BADGE_EVENT_NAME = ALERT_BADGE_EVENT;

interface NextDepartureInfo {
  iso: string;
  destination: string;
  /** Stable id for dedup: ISO time + destination */
  depId: string;
}

async function fetchNextDeparture(lat: number, lon: number, dockName: string): Promise<NextDepartureInfo | null> {
  try {
    const data = await edgeFn<{ nearbyStops: NearestStop[] }>('stops-nearest', {
      body: { lat, lon, radius: 5000, limit: 5, transportModes: ['water'] },
    });
    const match = findMatchingStop(data?.nearbyStops || [], dockName);
    if (!match?.departures?.length) return null;
    const now = Date.now();
    const upcoming = match.departures
      .map(d => ({ ...d, ms: new Date(d.time).getTime() }))
      .filter(d => d.ms > now)
      .sort((a, b) => a.ms - b.ms);
    const next = upcoming[0];
    if (!next) return null;
    return {
      iso: next.time,
      destination: next.destination,
      depId: `${next.time}__${next.destination}`,
    };
  } catch (err) {
    if (!(err instanceof EdgeFnError && err.code === 'ABORTED')) {
      console.warn('[alertTrigger] fetch failed for', dockName, err);
    }
    return null;
  }
}

export function useAlertTrigger() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { alerts, markTriggered } = useRouteAlerts();
  const { routes } = useFerryRoutes();
  // Local in-memory dedup (avoid re-firing within a single session even before persist returns)
  const firedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const activeAlerts = alerts.filter(a => a.active);
    if (activeAlerts.length === 0) return;

    let cancelled = false;

    const tick = async () => {
      if (cancelled || document.hidden) return;

      for (const alert of activeAlerts) {
        const route = routes.find(r => r.slug === alert.samband_slug);
        if (!route || route.fromLat === undefined || route.fromLon === undefined) continue;

        const next = await fetchNextDeparture(route.fromLat, route.fromLon, route.from);
        if (cancelled || !next) continue;

        const dedupKey = `${alert.id}__${next.depId}`;
        if (firedKeysRef.current.has(dedupKey)) continue;
        if (alert.last_triggered_departure_id === next.depId) {
          firedKeysRef.current.add(dedupKey);
          continue;
        }

        const msUntilDeparture = new Date(next.iso).getTime() - Date.now();
        const targetMs = alert.lead_time_minutes * 60_000;
        const diff = msUntilDeparture - targetMs;

        // Fire if within window AND we haven't already passed the departure
        if (diff <= 0 && diff >= -TRIGGER_WINDOW_MS && msUntilDeparture > -60_000) {
          firedKeysRef.current.add(dedupKey);
          const label = `${route.from} → ${route.to}`;
          const minsLeft = Math.max(0, Math.round(msUntilDeparture / 60_000));
          const depTime = formatClock(next.iso, language);

          toast(formatMessage(language, 'time_alert_title', {
            countdown: formatCountdownMinutes(minsLeft, language),
          }), {
            description: formatMessage(language, 'time_alert_description', {
              route: label,
              clock: depTime,
              destination: next.destination,
            }),
            duration: 10_000,
            action: {
              label: 'Se',
              onClick: () => navigate(`/ferge/samband/${alert.samband_slug}`),
            },
          });
          bumpUnseenBadge();
          trackEvent('route_alert_triggered' as Parameters<typeof trackEvent>[0], {
            route: alert.samband_slug,
            lead_time: alert.lead_time_minutes,
          });
          void markTriggered(alert.id, next.depId);
        }
      }
    };

    // Run once immediately, then on interval
    void tick();
    const interval = window.setInterval(tick, POLL_INTERVAL_MS);

    const onVisible = () => { if (!document.hidden) void tick(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [alerts, routes, navigate, markTriggered, language]);
}
